import { LinearGradient } from 'expo-linear-gradient';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ScrollView,
  StatusBar,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import BronzeMedal from '../assets/images/icons/bronze.svg';
import GoldMedal from '../assets/images/icons/gold.svg';
import SilverMedal from '../assets/images/icons/silver.svg';
import ActionCard, { Action } from './ActionCard';
import ActionInfoModal from './ActionInfoModal';
import MedalTier from './MedalTier';

interface ObjectiveSelectorProps {
  actions: Action[];
  onValidate: (selectedActions: number[]) => void;
  initialSelected?: number[];
}

const MEDAL_TIERS = [
  {
    icon: BronzeMedal,
    titleKey: 'editObjectives.bronzeTitle',
    rangeKey: 'editObjectives.bronzeRange',
    min: 1,
    max: 3,
  },
  {
    icon: SilverMedal,
    titleKey: 'editObjectives.silverTitle',
    rangeKey: 'editObjectives.silverRange',
    min: 4,
    max: 6,
  },
  {
    icon: GoldMedal,
    titleKey: 'editObjectives.goldTitle',
    rangeKey: 'editObjectives.goldRange',
    min: 7,
    max: 9,
  },
];

export default function ObjectiveSelector({
  actions,
  onValidate,
  initialSelected = [],
}: ObjectiveSelectorProps) {
  const { t } = useTranslation();
  const [selectedActions, setSelectedActions] =
    useState<number[]>(initialSelected);
  const [infoModalVisible, setInfoModalVisible] = useState(false);
  const [selectedInfo, setSelectedInfo] = useState<{
    name: string;
    description: string;
  } | null>(null);

  const toggleAction = (id: number) => {
    if (selectedActions.includes(id)) {
      setSelectedActions(selectedActions.filter((actionId) => actionId !== id));
    } else {
      setSelectedActions([...selectedActions, id]);
    }
  };

  const showInfo = (action: Action) => {
    setSelectedInfo({
      name: t(action.nameKey),
      description: t(action.descriptionKey),
    });
    setInfoModalVisible(true);
  };

  const getMedalHighlight = (min: number, max: number) => {
    const count = selectedActions.length;
    return count >= min && count <= max;
  };

  return (
    <SafeAreaView
      style={{ flex: 1, backgroundColor: '#FBEFE3' }}
      edges={['top']}
    >
      <StatusBar barStyle="dark-content" backgroundColor="#FBEFE3" />
      <LinearGradient
        colors={['#FBEFE3', '#FFFFFF']}
        locations={[0, 1]}
        style={styles.container}
      >
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
        >
          <Text style={styles.title}>{t('editObjectives.title')}</Text>
          <Text style={styles.subtitle}>
            {t('editObjectives.subtitle')}
          </Text>

          <View style={styles.medalsContainer}>
            {MEDAL_TIERS.map((tier, index) => (
              <MedalTier
                key={index}
                icon={tier.icon}
                title={t(tier.titleKey)}
                range={t(tier.rangeKey)}
                isHighlighted={getMedalHighlight(tier.min, tier.max)}
              />
            ))}
          </View>

          <View style={styles.actionsGrid}>
            {actions.map((action) => (
              <ActionCard
                key={action.id}
                action={action}
                isSelected={selectedActions.includes(action.id)}
                onPress={toggleAction}
                onInfoPress={showInfo}
              />
            ))}
          </View>

          <TouchableOpacity
            style={styles.validateButton}
            onPress={() => onValidate(selectedActions)}
          >
            <LinearGradient
              colors={['#1C453F', '#78A584']}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 0 }}
              style={styles.validateGradient}
            >
              <Text style={styles.validateText}>{t('common.validate')}</Text>
            </LinearGradient>
          </TouchableOpacity>
        </ScrollView>

        <ActionInfoModal
          visible={infoModalVisible}
          title={selectedInfo?.name || null}
          description={selectedInfo?.description || null}
          onClose={() => setInfoModalVisible(false)}
        />
      </LinearGradient>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingTop: 24,
  },
  scrollContent: {
    padding: 24,
    paddingTop: 0,
    paddingBottom: 24,
  },
  title: {
    fontFamily: 'Inter_700Bold',
    fontSize: 28,
    color: '#000',
    marginBottom: 12,
  },
  subtitle: {
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    color: '#666',
    marginBottom: 24,
    lineHeight: 20,
  },
  medalsContainer: {
    marginBottom: 24,
    borderRadius: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2,
    overflow: 'hidden',
  },
  actionsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  validateButton: {
    borderRadius: 16,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  validateGradient: {
    paddingVertical: 16,
    alignItems: 'center',
  },
  validateText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 16,
    color: '#FFF',
  },
});
